// EPSE Profiles Hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EPSEProfile, EPSEQuarterData } from '@/types/epse';
import { calculateProfileStatistics, buildNormalizedShape, groupByContext } from '@/lib/epseStatistics';
import { validateQuarterData } from '@/lib/epseDataValidation';
import { toast } from 'sonner';

export function useEPSEProfiles(siteId: string | undefined) {
  const queryClient = useQueryClient();
  
  const { data: profiles, isLoading, error } = useQuery({
    queryKey: ['epse-profiles', siteId],
    queryFn: async () => {
      if (!siteId) return [];
      
      const { data, error } = await supabase
        .from('epse_profiles')
        .select('*')
        .eq('site_id', siteId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as EPSEProfile[];
    },
    enabled: !!siteId,
  });
  
  const generateProfiles = useMutation({
    mutationFn: async ({
      siteId,
      quarterData,
      annualKwh,
    }: {
      siteId: string;
      quarterData: EPSEQuarterData[];
      annualKwh?: number;
    }) => {
      // Delete existing profiles for this site
      await supabase
        .from('epse_profiles')
        .delete()
        .eq('site_id', siteId);
      
      // Validate data
      const qualityReport = validateQuarterData(quarterData, annualKwh);
      
      // Generate overall profile
      const overallStats = calculateProfileStatistics(quarterData);
      const overallShape = buildNormalizedShape(quarterData);
      
      const profilesToInsert: Omit<EPSEProfile, 'id' | 'created_at' | 'updated_at'>[] = [{
        site_id: siteId,
        profile_type: 'measured',
        day_type: null,
        season: null,
        normalized_shape: overallShape,
        statistics: overallStats,
        data_quality_score: qualityReport.coverage_score,
      }];
      
      // Generate profiles per day_type + season combination
      const grouped = groupByContext(quarterData);
      grouped.forEach((groupData, key) => {
        const [dayType, season] = key.split('_');
        if (dayType === 'unknown' || season === 'unknown') return;
        
        const stats = calculateProfileStatistics(groupData);
        const shape = buildNormalizedShape(groupData);
        
        profilesToInsert.push({
          site_id: siteId,
          profile_type: 'measured',
          day_type: dayType,
          season: season,
          normalized_shape: shape,
          statistics: stats,
          data_quality_score: null,
        });
      });
      
      // Insert all profiles
      const { error } = await supabase
        .from('epse_profiles')
        .insert(profilesToInsert.map(p => ({
          site_id: p.site_id,
          profile_type: p.profile_type,
          day_type: p.day_type,
          season: p.season,
          normalized_shape: p.normalized_shape as any,
          statistics: p.statistics as any,
          data_quality_score: p.data_quality_score,
        })));
      
      if (error) throw error;
      
      return {
        profileCount: profilesToInsert.length,
        qualityReport,
      };
    },
    onSuccess: ({ profileCount, qualityReport }) => {
      queryClient.invalidateQueries({ queryKey: ['epse-profiles'] });
      
      if (qualityReport.warnings.length > 0) {
        const errorWarnings = qualityReport.warnings.filter(w => w.severity === 'error');
        if (errorWarnings.length > 0) {
          toast.warning(`${profileCount} profielen gegenereerd met ${errorWarnings.length} kritieke waarschuwing(en)`);
        } else {
          toast.success(`${profileCount} profielen gegenereerd`);
        }
      } else {
        toast.success(`${profileCount} profielen gegenereerd`);
      }
    },
    onError: (error) => {
      toast.error(`Fout bij genereren profielen: ${error.message}`);
    },
  });
  
  return {
    profiles,
    isLoading,
    error,
    generateProfiles,
  };
}
